import Box from "@material-ui/core/esm/Box";
import Button from "@material-ui/core/esm/Button";
import Info from "@material-ui/icons/esm/Info";
import InfoOutlined from "@material-ui/icons/esm/InfoOutlined";
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';
import { EnumInvoiceStatus } from 'api';
import getCreditCardFee from 'api/invoices/getCreditCardFee';
import getInvoiceByInvoiceNumber from 'api/invoices/getInvoiceByInvoiceNumber';
import { DownloadButton, BackButton } from 'components/Button';
import Dialog from 'components/Dialog/Dialog';
import Divider from 'components/Divider/Divider';
import InfoBox from 'components/InfoBox/InfoBox';
import Spinner from 'components/Spinner/Spinner';
import { LargeHeader, Header, Title } from 'components/Text';
import { LIGHT_PRIMARY } from 'constants/colors';
import Language from 'constants/language';
import { archivedInvoiceLinesForInvoiceUrl } from 'constants/urls';
import useDownloadInvoice from 'hooks/invoices/useDownloadInvoice';
import useRequestPaymentLink from 'hooks/invoices/useRequestPaymentLink';
import { INVOICES } from 'routes/invoices/paths';
import downloadBlob from 'utils/download/downloadBlob';
import isInvoiceArchived from 'utils/invoices/isInvoiceArchived';
import checkLoadable from 'utils/recoil/checkLoadable';
import InvoiceDetailsRow from './InvoiceDetailsRows';
import InvoicePassings from './InvoicePassings';
import RetmittanceDetails from './RemittanceDetails';
export default function InvoiceDetails() {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    invoiceNumber
  } = useParams();
  const [isDowloadingArchive, setIsDownloadingArchive] = useState(false);
  const invoiceLoadable = useRecoilValueLoadable(getInvoiceByInvoiceNumber(invoiceNumber));
  const {
    contents: invoice,
    loading
  } = checkLoadable(invoiceLoadable);
  const {
    requestPaymentLink,
    paymentLinkIsLoading
  } = useRequestPaymentLink(invoice);
  const [openPayNowWarning, setOpenPayNowWarning] = useState(false);
  const creditCardFeeLoadable = useRecoilValueLoadable(getCreditCardFee);
  const {
    contents: creditCardFee
  } = checkLoadable(creditCardFeeLoadable);
  const {
    downloadInvoice,
    downloadIsLoading
  } = useDownloadInvoice(invoice);
  const isRemittance = invoice?.invoiceStatus === EnumInvoiceStatus.Remittance;
  const isInvoiceNotDownloadable = invoice?.invoiceStatus === EnumInvoiceStatus.Pending || invoice?.invoiceStatus === EnumInvoiceStatus.Cancelled || invoice?.invoiceStatus === EnumInvoiceStatus.Preparing || invoice?.invoiceStatus === EnumInvoiceStatus.Initial || invoice?.invoiceStatus === EnumInvoiceStatus.SentToDistribution || invoice?.invoiceStatus === EnumInvoiceStatus.PreparingDistribution || invoice?.invoiceStatus === EnumInvoiceStatus.AwaitingDelivery || invoice?.invoiceStatus === EnumInvoiceStatus.DocumentError || invoice?.invoiceStatus === EnumInvoiceStatus.ValidationError;
  const isInvoicePayableNow = (invoice?.invoiceStatus === EnumInvoiceStatus.PaymentRequested || invoice?.invoiceStatus === EnumInvoiceStatus.PaymentReminderSent || invoice?.invoiceStatus === EnumInvoiceStatus.ExternalDebtCollection) && invoice.totalPriceVatIncluded && parseFloat(invoice.totalPriceVatIncluded) <= 5500;
  if (loading) return /*#__PURE__*/React.createElement(Spinner, null);
  if (!invoice) return /*#__PURE__*/React.createElement(Title, null, t('invoices.no_available_invoice_details'));
  const content = isRemittance ? invoice.invoiceId && /*#__PURE__*/React.createElement(RetmittanceDetails, {
    invoiceLines: invoice.invoiceLines ?? []
  }) : invoice.invoiceId && /*#__PURE__*/React.createElement(InvoicePassings, {
    invoiceId: invoice.invoiceId || '',
    invoiceNumber: invoiceNumber,
    agreementName: invoice.agreementName || '',
    isInvoiceArchived: isInvoiceArchived(invoice)
  });
  const downloadArchivedInvoiceLines = async () => {
    const lang = i18n.language;
    const useNorwegian = lang === Language.norwegian;
    try {
      setIsDownloadingArchive(true);
      await downloadBlob(archivedInvoiceLinesForInvoiceUrl(invoiceNumber, useNorwegian), `${t('invoices.archived_invoice_lines_filename')}_${invoiceNumber}.csv`);
    } catch {
      enqueueSnackbar(t('error.archived_invoice_lines'), {
        variant: 'error'
      });
    } finally {
      setIsDownloadingArchive(false);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackButton, {
    to: INVOICES
  }, /*#__PURE__*/React.createElement(LargeHeader, null, `${t('invoices.invoice')}`)), /*#__PURE__*/React.createElement(Box, {
    mb: 2
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Header, null, `${t('invoices.invoice_number')}: ${invoice.invoiceNumber}`), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row"
  }, /*#__PURE__*/React.createElement(Dialog, {
    open: openPayNowWarning,
    onClose: () => setOpenPayNowWarning(false),
    dialogTitle: t('invoices.credit_card_fee_warning_title'),
    onPrimaryBtnClick: () => {
      requestPaymentLink();
      setOpenPayNowWarning(false);
    },
    primaryBtnTitle: t('buttons.pay_now'),
    isLoading: paymentLinkIsLoading,
    primaryBtnClickIsDisabled: false
  }, /*#__PURE__*/React.createElement(Box, {
    fontSize: "medium"
  }, t('invoices.credit_card_fee_warning', {
    creditCardFee
  })), /*#__PURE__*/React.createElement(Box, {
    bgcolor: LIGHT_PRIMARY,
    padding: "2rem",
    borderRadius: "5px",
    fontSize: "medium",
    position: "relative",
    textAlign: "center",
    mt: 2
  }, /*#__PURE__*/React.createElement(InfoOutlined, {
    style: {
      position: 'absolute',
      top: '1rem',
      left: '1rem'
    }
  }), t('invoices.payment_invoice_customer_information_box'))), isInvoicePayableNow && /*#__PURE__*/React.createElement(Button, {
    size: "large",
    style: {
      color: 'red',
      fontWeight: 'bold'
    },
    onClick: () => setOpenPayNowWarning(true),
    disabled: paymentLinkIsLoading
  }, paymentLinkIsLoading ? /*#__PURE__*/React.createElement(Spinner, {
    isButtonProgress: true
  }) : `💳  ${t('buttons.pay_now')}`), /*#__PURE__*/React.createElement(DownloadButton, {
    onClick: () => downloadInvoice(),
    loading: downloadIsLoading,
    disabled: isInvoiceNotDownloadable
  }))), isInvoiceArchived(invoice) && /*#__PURE__*/React.createElement(InfoBox, null, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: 2
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    gridColumnGap: 8
  }, /*#__PURE__*/React.createElement(Info, null), t('invoices.archived_info')), /*#__PURE__*/React.createElement(DownloadButton, {
    onClick: downloadArchivedInvoiceLines,
    loading: isDowloadingArchive,
    disabled: isDowloadingArchive
  }, t('invoices.download_archived_data')))), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(InvoiceDetailsRow, invoice), /*#__PURE__*/React.createElement(Divider, null)), /*#__PURE__*/React.createElement(Box, {
    mt: 2
  }, /*#__PURE__*/React.createElement(Header, null, isRemittance ? t('remittance.details_text') : t('passings.passings')), content));
}